import { Stack } from "@mui/joy";
import { memo, useMemo } from "react";
import { useMediaDataRoom } from "features/mediaDataRoom/contexts";
import {
  MediaDataRoomParticipantRole,
  MediaDataRoomVersion,
} from "features/mediaDataRoom/models";
import { CollaborationTypes } from "models/dataRoom";
import MediaDataRoomConfiguration, {
  type MediaDataRoomConfigurationProps,
} from "./components/MediaDataRoomConfiguration";

const MediaDataRoomAbConfigurationTab = memo(() => {
  const {
    dataRoomName,
    createdAt,
    createdBy,
    matchingIdFormat,
    publisherLogo,
    advertiserLogo,
    isAdvertiser,
    isPublisher,
    isAgency,
    isObserver,
    isDataPartner,
    seedAudienceProviderName,
    advertiserUserEmails,
    publisherUserEmails,
    agencyUserEmails,
    observerUserEmails,
    dataPartnerUserEmails,
    baseAudienceProviderName,
    hashMatchingIdWith,
    dataRoomId,
    supportedFeatures: {
      enableInsights,
      enableLookalike,
      enableRuleBasedAudiences,
      enableRemarketing,
      showAbsoluteValues,
    },
  } = useMediaDataRoom(MediaDataRoomVersion.ab);
  const configurationProps = useMemo<MediaDataRoomConfigurationProps>(() => {
    return {
      collaborationTypes: [
        ...(enableInsights ? [CollaborationTypes.Insights] : []),
        ...(enableRemarketing ? [CollaborationTypes.Remarketing] : []),
        ...(enableRuleBasedAudiences
          ? [CollaborationTypes.RuleBasedAudiences]
          : []),
        ...(enableLookalike ? [CollaborationTypes.Lookalike] : []),
      ],
      createdAt,
      createdBy,
      dataRoomId,
      dataRoomName,
      hashMatchingIdWith: hashMatchingIdWith,
      matchingIdFormat: matchingIdFormat,
      participants: [
        {
          emails: advertiserUserEmails,
          id: "advertiser",
          isCurrentUser: isAdvertiser,
          logo: advertiserLogo,
          name: seedAudienceProviderName,
          permissions: [],
          role: MediaDataRoomParticipantRole.Advertiser,
        },
        {
          emails: publisherUserEmails,
          id: "publisher",
          isCurrentUser: isPublisher,
          logo: publisherLogo,
          name: baseAudienceProviderName,
          permissions: [],
          role: MediaDataRoomParticipantRole.Publisher,
        },
        ...(dataPartnerUserEmails.length
          ? [
              {
                emails: dataPartnerUserEmails,
                id: "dataPartner",
                isCurrentUser: isDataPartner,
                name: `<${dataPartnerUserEmails[0].split("@")[1]}>`,
                permissions: [],
                role: MediaDataRoomParticipantRole.DataPartner,
              },
            ]
          : []),
        ...(agencyUserEmails.length
          ? [
              {
                emails: agencyUserEmails,
                id: "agency",
                isCurrentUser: isAgency,
                name: `<${agencyUserEmails[0].split("@")[1]}>`,
                permissions: [],
                role: MediaDataRoomParticipantRole.Agency,
              },
            ]
          : []),
        ...(observerUserEmails.length
          ? [
              {
                emails: observerUserEmails,
                id: "observer",
                isCurrentUser: isObserver,
                name: `<${observerUserEmails[0].split("@")[1]}>`,
                permissions: [],
                role: MediaDataRoomParticipantRole.Observer,
              },
            ]
          : []),
      ],
      showAbsoluteValues,
    };
  }, [
    enableInsights,
    enableLookalike,
    dataPartnerUserEmails,
    agencyUserEmails,
    observerUserEmails,
    isAgency,
    isDataPartner,
    isObserver,
    isPublisher,
    publisherLogo,
    publisherUserEmails,
    enableRemarketing,
    baseAudienceProviderName,
    advertiserLogo,
    advertiserUserEmails,
    createdBy,
    isAdvertiser,
    seedAudienceProviderName,
    enableRuleBasedAudiences,
    createdAt,
    hashMatchingIdWith,
    matchingIdFormat,
    dataRoomName,
    showAbsoluteValues,
    dataRoomId,
  ]);
  return (
    <Stack sx={{ gap: 2, mx: 6 }}>
      <MediaDataRoomConfiguration {...configurationProps} />
    </Stack>
  );
});
MediaDataRoomAbConfigurationTab.displayName = "MediaDataRoomAbConfigurationTab";

export default MediaDataRoomAbConfigurationTab;
